import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";

const pickType = () => ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
const extOf = (t) => (t.includes("ogg") ? "ogg" : t.includes("mp4") ? "m4a" : "webm");

export default function VoiceRecorder({ voice, setVoice, onRecording }) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef([]);
  const recRef = useRef(null);
  const url = useMemo(() => (voice ? URL.createObjectURL(voice) : ""), [voice]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  useEffect(() => () => recRef.current?.stream.getTracks().forEach((t) => t.stop()), []);
  useEffect(() => { if (!rec) return; const t = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(t); }, [rec]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = pickType();
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const mt = mr.mimeType || type || "audio/webm";
        const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
        setVoice(new File(chunks.current, `vocale-${stamp}.${extOf(mt)}`, { type: mt }));
      };
      mr.start();
      setSecs(0);
      setRec(mr);
      recRef.current = mr;
      onRecording?.(true);
    } catch (e) { toast.error("Microfono non disponibile: consenti l'accesso al microfono"); }
  };
  const stop = () => { rec?.stop(); setRec(null); recRef.current = null; onRecording?.(false); };

  if (voice) return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 p-2" data-testid="chat-voice-ready">
      <audio src={url} controls className="h-8 flex-1" data-testid="chat-voice-player" />
      <button onClick={() => setVoice(null)} title="Elimina vocale" className="text-muted-foreground hover:text-destructive" data-testid="chat-voice-delete"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
  return rec ? (
    <button onClick={stop} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[hsl(0_72%_55%)] px-3 py-2 text-sm font-bold text-white" data-testid="chat-voice-stop">
      <Square className="h-4 w-4 animate-pulse" /> Ferma registrazione · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
    </button>
  ) : (
    <button onClick={start} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium hover:bg-white/5" data-testid="chat-voice-record">
      <Mic className="h-4 w-4" /> Registra messaggio vocale
    </button>
  );
}
